import './dashboard.scss';
import './dashboard-layout.scss';
import { CustomTooltip } from '@/components/custom-tooltip';
import type { AskRevFilter } from '@/types/askRevFilters';
import { useInterpretAskRevQuery } from '@/utils/askrev/query-interpreter';
import {
	getTabDisplayName,
	resolveTabFromFields,
} from '@/utils/askrev/tab-resolver';
import MuiAlert, {
	type AlertColor,
	type AlertProps,
} from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import styles from 'common/_variables.module.scss';
import { DropdownMenu } from 'components/DropdownMenu/DropdownMenu';
import type { menuType } from 'components/DropdownMenu/DropdownMenu';
import { Modal } from 'components/Modal/Modal';
import RouterPromptModal from 'components/RouterPromptModal/RouterPromptModal';
import { setDefaultSearchId } from 'features/header/redux/header-slice';
import { selectEnableSellOutFlag } from 'features/header/redux/selectors';
import type {
	DeleteSearchMutation,
	SaveSearchMutation,
} from 'features/my-view/gql/_gen_/rec-review.gql';
import {
	setQueryInput,
	setRecReviewTab,
	setSearchId,
	setShowSearchPanel,
	setTableSort,
} from 'features/my-view/redux/my-view-slice';
import {
	selectRecPriceChangeSearchId,
	selectRecReviewTab,
	selectSearchId,
	selectShowSearchPanel,
} from 'features/my-view/redux/selectors';
import { selectProperty } from 'features/property/redux/selectors';
import { useUser } from 'features/users/context/userContext';
import type { FormikProps } from 'formik';
import type {
	GetAskRevQueryResult,
	SearchInfo,
	SearchMetric,
	SearchMetricInfo,
	UserSearch,
} from 'graphql/gql-types';
import { toFixedNumber } from 'helpers/math-helpers';
import { useAskRevHistory, useSaveAskRevQuery } from 'hooks/useAskRevHistory';
import { useDeleteSearch } from 'hooks/useDeleteSearch';
import { useCannedSearches } from 'hooks/useUserSearches';
import { labels } from 'locales/en.label';
import { omit } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdEdit, MdManageSearch, MdPlaylistAdd } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { today } from '../../features/dates/date-helpers';
import { BulkRateForm } from '../../features/rec-rates/components/bulk-rate-form';
import { REC_STATUS_ALL } from '../../features/rec-rates/rec-rate-helpers';
import { DOW_ALL } from '../../features/search/search-helpers';
import {
	useSaveSearch,
	useSearchMetrics,
	useUserSearches,
} from '../../hooks/useUserSearches';
import { DEFAULT_SEARCH_ID, type Search } from '../../types/SearchTypes';
import { AskRev } from './AskRev/AskRev';
import { DashboardContent } from './DashboardContent';
import { RecReviewSearch } from './RecReviewSearch/RecReviewSearch';
import { useDashboardController } from './useDashboardController';
type InfoInput = {
	message: string;
	messageType: AlertColor;
};
const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
	return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />;
});
export const DashboardWrapper = () => {
	const dispatch = useAppDispatch();
	const history = useHistory();
	const { user } = useUser();
	const activeTab = useAppSelector(selectRecReviewTab);
	const { data: cannedSearchList } = useCannedSearches();
	const showSearchPanel = useAppSelector(selectShowSearchPanel);
	const enableSellOutFlag = useAppSelector(selectEnableSellOutFlag);
	const property = useAppSelector(selectProperty);
	const { propertyId } = property;
	const [showConfirmMessage, setShowConfirmMessage] = useState(false);
	const [saveSearchName, setSaveSearchName] = useState('');
	const searchId = useAppSelector(selectSearchId);
	// Get default search ID directly from canned searches
	const defaultSearchId = React.useMemo(() => {
		if (cannedSearchList?.getCannedSearchIds?.length > 0) {
			const defaultId = cannedSearchList.getCannedSearchIds.find(
				(item: { search_name?: string; search_id?: number }) =>
					item?.search_name === labels.rec_review.search.search_names.default,
			)?.search_id;
			// Store in Redux for other components that might need it
			if (defaultId) {
				dispatch(setDefaultSearchId(defaultId));
				return defaultId;
			}
		}
		return DEFAULT_SEARCH_ID;
	}, [cannedSearchList, dispatch]);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
		useState<boolean>(false);
	const [info, setInfo] = useState<InfoInput>({
		message: '',
		messageType: 'error',
	});
	const { deleteSearch, loading: deleteLoading } = useDeleteSearch();
	const { interpretQuery, queryLoading } = useInterpretAskRevQuery();
	const { saveAskRevQuery } = useSaveAskRevQuery();
	const { data: queryHistoryData } = useAskRevHistory({
		propertyId: property.propertyId,
		userId: user?.id,
	});
	const EMPTY_SEARCH: Search = {
		duration: 30,
		isEditable: true,
		selectedDays: DOW_ALL,
		searchId: undefined,
		searchName: '',
		searchType: '',
		startDate: today(),
		userId: user?.id,
		statuses: REC_STATUS_ALL,
		searchCriteria: [
			{ lhsCode: '', operatorCode: '', rhsCode: '', rhsCustomValue: 0 },
		] as SearchMetricInfo[],
	};
	const searchFormRef = useRef<FormikProps<UserSearch>>(null);
	const { userSearches, loadingUserSearches } = useUserSearches(user?.id);
	const recPriceChangeSearchId = useAppSelector(selectRecPriceChangeSearchId);
	const [showSearch, setShowSearch] = useState(false);
	const [showBulkForm, setShowBulkForm] = useState(false);
	const { searchMetrics } = useSearchMetrics();
	const [searchDetails, setSearchDetails] = useState(EMPTY_SEARCH);
	const [currentSearch, setCurrentSearch] = useState(EMPTY_SEARCH);
	const [isFormChanged, setIsFormChanged] = useState(false);
	const [askRevFilters, setAskRevFilters] = useState<AskRevFilter[]>([]);
	const [askQuerySummary, setAskQuerySummary] = useState<string>('');
	const [askRevSuggestion, setAskRevSuggestion] = useState<string>('');
	const [askRevTabHint, setAskRevTabHint] = useState<string>('');
	const [selectedValue, setSelectedValue] = useState<string>('');
	// NEW: AI-suggested sorting parameters
	const [aiSortBy, setAiSortBy] = useState<string | null>(null);
	const [aiSortDirection, setAiSortDirection] = useState<'asc' | 'desc' | null>(
		null,
	);
	const [aiSortLimit, setAiSortLimit] = useState<number | null>(null);
	// Get compSet from useDashboardController for AskRev queries
	const { compSet } = useDashboardController({
		propertyId,
		askRevFilters,
		// NEW: Pass AI sorting parameters to dashboard controller
		aiSortBy,
		aiSortDirection,
		aiSortLimit,
	});
	const newSearch: Search = {
		duration: 30,
		isEditable: true,
		selectedDays: DOW_ALL,
		searchName: '',
		searchType: 'new',
		startDate: today(),
		userId: user?.id,
		statuses: REC_STATUS_ALL,
		searchCriteria: [],
	};

	// Initialize AI sorting parameters to null on component mount
	useEffect(() => {
		setAiSortBy(null);
		setAiSortDirection(null);
		setAiSortLimit(null);
	}, []); // Empty deps - run once on mount

	// Reset AI sorting parameters on mount and when propertyId changes
	useEffect(() => {
		setAiSortBy(null);
		setAiSortDirection(null);
		setAiSortLimit(null);
	}, [propertyId]);

	useEffect(() => {
		if (!isFormChanged && searchDetails.searchType !== 'new') {
			dispatch(
				setQueryInput({
					searchId,
					userId: user?.id,
					startDate: searchDetails.startDate,
					duration: searchDetails.duration,
					statuses: searchDetails.statuses,
					selectedDays: searchDetails.selectedDays,
					searchName: searchDetails.searchName,
					searchCriteria: searchDetails.searchCriteria as SearchMetricInfo[],
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchId, user?.id, searchDetails]);
	useEffect(() => {
		const defaultInput: SearchInfo = {
			userId: user?.id,
			duration: 365,
			selectedDays: DOW_ALL,
			startDate: today(),
			statuses: REC_STATUS_ALL,
		};
		if (enableSellOutFlag) {
			setShowSearch(true);
			dispatch(setQueryInput(defaultInput));
		} else {
			setShowSearch(false);
			dispatch(setQueryInput(currentSearch));
			dispatch(setShowSearchPanel(false));
		}
	}, [currentSearch, dispatch, enableSellOutFlag, user?.id]);
	const [isBlocked, setIsBlocked] = useState(false);
	const onConfirmationWindowClose = useCallback(
		() => setIsConfirmationModalOpen(false),
		[],
	);
	const onSearchSaveSuccess = useCallback(
		(data: SaveSearchMutation) => {
			if (data.saveSearch?.searchId) {
				setInfo({
					message: labels.rec_review.search.save_search_msg,
					messageType: 'success',
				});
			}
			setShowConfirmMessage(true);
			showSearchPanel && dispatch(setShowSearchPanel(false));
		},
		[dispatch, showSearchPanel],
	);
	const { saveSearch, saveSearchLoading } = useSaveSearch(onSearchSaveSuccess);
	const hideConfirmMessage = () => {
		setShowConfirmMessage(false);
	};
	const onSearch = () => {
		if (searchFormRef.current) {
			const values = {
				duration: searchFormRef.current.values.duration,
				endDate: searchFormRef.current.values.endDate,
				searchName: searchFormRef.current.values.searchName,
				selectedDays: searchFormRef.current.values.selectedDays,
				startDate: searchFormRef.current.values.startDate,
				userId: searchFormRef.current.values.userId,
				statuses: searchFormRef.current.values.statuses,
				searchCriteria: convertRhsCustomValueToNumber(
					searchFormRef.current.values.searchCriteria as SearchMetricInfo[],
				),
			};
			setIsFormChanged(searchFormRef.current.dirty);
			dispatch(setQueryInput({ ...values }));
		}
	};
	const convertRhsCustomValueToNumber = (data?: SearchMetricInfo[]) => {
		if (!data) return;
		const fractionDigits: Record<string, number> = {
			available_rooms: 0,
			rec_rate: 2,
			market_rate: 2,
			current_rate: 2,
			current_otb: 0,
			total_fcst: 0,
			adr: 2,
			occ_pct: 1,
			fcst_occ_pct: 1,
		};
		return data.map(({ rhsCustomValue, ...rest }) => {
			const { lhsCode } = rest;
			if (typeof rhsCustomValue === 'string')
				rhsCustomValue = Number.parseFloat(rhsCustomValue);
			if (Object.prototype.hasOwnProperty.call(fractionDigits, lhsCode)) {
				rhsCustomValue = toFixedNumber(rhsCustomValue, fractionDigits[lhsCode]);
			}
			return {
				...rest,
				rhsCustomValue,
			};
		});
	};
	const onSearchSaveAs = () => {
		if (searchFormRef.current) {
			const values = {
				duration: searchFormRef.current.values.duration,
				endDate: searchFormRef.current.values.endDate,
				searchName: searchFormRef.current.values.searchName,
				selectedDays: searchFormRef.current.values.selectedDays,
				startDate: searchFormRef.current.values.startDate,
				userId: user?.id,
				statuses: searchFormRef.current.values.statuses,
				searchCriteria: convertRhsCustomValueToNumber(
					searchFormRef.current.values.searchCriteria as SearchMetricInfo[],
				),
			};
			saveSearch({ variables: { search: values } });
			values.searchName && setSaveSearchName(values.searchName);
		}
	};
	const onDelete = () => {
		if (searchFormRef.current) {
			deleteSearch({
				variables: { searchId: searchFormRef.current.values.searchId },
				onCompleted: (data: DeleteSearchMutation) => {
					if (data.deleteSearch?.success) {
						setInfo({
							message: labels.rec_review.search.delete_search_msg_success,
							messageType: 'success',
						});
						dispatch(setSearchId(defaultSearchId || DEFAULT_SEARCH_ID));
						setShowConfirmMessage(true);
					} else {
						setInfo({
							message: labels.rec_review.search.delete_search_msg_error,
							messageType: 'error',
						});
					}
				},
			});
		}
	};
	const onSearchSave = () => {
		if (searchFormRef.current) {
			const values = {
				duration: searchFormRef.current.values.duration,
				endDate: searchFormRef.current.values.endDate,
				searchId: searchFormRef.current.values.searchId,
				searchName: searchFormRef.current.values.searchName,
				selectedDays: searchFormRef.current.values.selectedDays,
				startDate: searchFormRef.current.values.startDate,
				userId: searchFormRef.current.values.userId,
				statuses: searchFormRef.current.values.statuses,
				searchCriteria: convertRhsCustomValueToNumber(
					searchFormRef.current.values.searchCriteria as SearchMetricInfo[],
				)?.map((x) => omit(x, ['__typename', 'searchId'])),
			};
			saveSearch({ variables: { search: values } });
			values.searchName && setSaveSearchName(values.searchName);
		}
	};
	// Helper function to compare two filter arrays for equality
	const areFiltersEqual = (
		filters1: AskRevFilter[],
		filters2: AskRevFilter[],
	): boolean => {
		if (filters1.length !== filters2.length) return false;

		// Sort both arrays by field+operator+value for comparison
		const sortFilter = (a: AskRevFilter, b: AskRevFilter) =>
			`${a.field}_${a.operator}_${String(a.value)}`.localeCompare(
				`${b.field}_${b.operator}_${String(b.value)}`,
			);

		const sorted1 = [...filters1].sort(sortFilter);
		const sorted2 = [...filters2].sort(sortFilter);

		return sorted1.every(
			(filter, index) =>
				filter.field === sorted2[index].field &&
				filter.operator === sorted2[index].operator &&
				String(filter.value) === String(sorted2[index].value),
		);
	};

	// Check if current filters already exist in recent queries
	const isFiltersDuplicate = (newFilters: AskRevFilter[]): boolean => {
		if (!queryHistoryData?.getAskRevQueryHistory) return false;

		return queryHistoryData.getAskRevQueryHistory.some(
			(historyQuery: GetAskRevQueryResult) => {
				const historyFilters = Array.isArray(historyQuery.filters)
					? historyQuery.filters
					: [];
				return areFiltersEqual(newFilters, historyFilters);
			},
		);
	};
	const handleClearAskRev = () => {
		setAskRevFilters([]);
		setAskQuerySummary('');
		setAskRevSuggestion('');
		setAskRevTabHint('');
		// Clear AI sorting parameters and reset to default date sort
		setAiSortBy(null);
		setAiSortDirection(null);
		setAiSortLimit(null);
		// Set table sort to date ascending (oldest to newest)
		dispatch(setTableSort({ column: 'stay_date', direction: 'asc' }));
	};

	const submitAskRevQuery = async (
		query: string,
		existingFilters?: AskRevFilter[],
		suggestion?: string,
		querySummary?: string,
	) => {
		try {
			// If filters are passed (from query history), use them directly without interpreting
			if (existingFilters && existingFilters.length > 0) {
				setAskRevFilters(existingFilters);
				setAskRevSuggestion(suggestion || '');

				const targetTab = resolveTabFromFields(existingFilters);
				if (targetTab && targetTab !== activeTab) {
					dispatch(setRecReviewTab(targetTab));
					history.push(`/my-view/${targetTab}`);
					setAskRevTabHint(
						`Switched to the ${getTabDisplayName(targetTab)} tab to show relevant columns.`,
					);
				} else {
					setAskRevTabHint('');
				}
				setAskQuerySummary(querySummary || 'Loaded from query history');
				return;
			}

			// Otherwise, interpret the new query
			// Transform compSet to only include hotelId and name (CompetitorInput format)
			const competitorInput = compSet?.map((comp) => ({
				hotelId: comp.hotelId,
				name: comp.name,
			}));

			console.log('[AskRev] Submitting query:', {
				query,
				hasCompSet: !!competitorInput,
				compSetSize: competitorInput?.length,
			});

			const result = await interpretQuery(query, competitorInput);
			if (result) {
				console.log('[AskRev] AI Response:', {
					sortBy: result.sortBy,
					sortDirection: result.sortDirection,
					sortLimit: result.sortLimit,
					filterCount: result.filters.length,
					summary: result.summary,
				});

				setAskRevFilters(result.filters);
				setAskRevSuggestion(result.suggestion || '');
				// Map backend field names to frontend grid column IDs
				const fieldMapping: Record<string, string> = {
					adr: 'adr_live',
					adr_live: 'adr_live',
					current_rate: 'current_rate_live',
					current_rate_live: 'current_rate_live',
					current_revenue: 'current_revenue_live',
					current_revenue_live: 'current_revenue_live',
					revpar: 'revpar_live',
					revpar_live: 'revpar_live',
					occ_pct: 'occ_pct_live',
					occ_pct_live: 'occ_pct_live',
					current_otb: 'current_otb_live',
					current_otb_live: 'current_otb_live',
					available_rooms: 'available_rooms_live',
					available_rooms_live: 'available_rooms_live',
				};
				// Apply mapping to sortBy field
				const mappedSortBy = result.sortBy
					? fieldMapping[result.sortBy] || result.sortBy
					: null;

				console.log('[AskRev] Mapped sortBy:', {
					original: result.sortBy,
					mapped: mappedSortBy,
				});

				setAiSortBy(mappedSortBy);
				setAiSortDirection(result.sortDirection || null);
				setAiSortLimit(result.sortLimit || null);

				const targetTab = resolveTabFromFields(result.filters, mappedSortBy);
				if (targetTab && targetTab !== activeTab) {
					dispatch(setRecReviewTab(targetTab));
					history.push(`/my-view/${targetTab}`);
					setAskRevTabHint(
						`Switched to the ${getTabDisplayName(targetTab)} tab to show relevant columns.`,
					);
				} else {
					setAskRevTabHint('');
				}
				setAskQuerySummary(result.summary);

				// Check for duplicate filters before saving
				const isDuplicate = isFiltersDuplicate(result.filters);

				if (isDuplicate) {
					return; // Don't save duplicate query
				}

				// Save the query after successful interpretation only if not duplicate
				try {
					// Transform filters to match GraphQL FilterInput schema (value must be string)
					const transformedFilters = result.filters.map((filter) => ({
						field: filter.field,
						operator: filter.operator,
						value: String(filter.value), // Convert value to string
					}));

					await saveAskRevQuery({
						variables: {
							query: {
								query_text: query,
								property_id: property.propertyId,
								user_id: user?.id || '',
								filters: transformedFilters,
								suggestion: result.suggestion || '',
								summary: result.summary,
							},
						},
					});
				} catch (saveError) {
					console.error('Error saving query:', saveError);
				}
			}
		} catch (error) {
			console.error('Error interpreting query:', error);
		}
	};
	// TODO: Need to check if this is required
	useEffect(() => {
		if (userSearches && searchId && searchId > 0) {
			const search_details = userSearches.find(
				(item) => item?.searchId === searchId,
			) as Search;
			if (search_details) {
				setSearchDetails(search_details);
				setCurrentSearch(search_details);
			}
		}
		if (saveSearchName !== '') {
			const result = userSearches?.filter((obj) => {
				return obj?.searchName === saveSearchName;
			});
			const id = result ? result[0]?.searchId : 0;
			id && dispatch(setSearchId(id));
		}
	}, [dispatch, saveSearchName, searchId, userSearches]);
	const onSearchClick = () => {
		if (searchFormRef?.current?.dirty) {
			setIsConfirmationModalOpen(true);
		} else {
			onClose();
		}
	};
	const onClose = () => {
		dispatch(setShowSearchPanel(!showSearchPanel));
	};

	const onBulkFormToggle = () => {
		setShowBulkForm(!showBulkForm);
		// Close search panel if it's open
		if (showSearchPanel) {
			dispatch(setShowSearchPanel(false));
		}
	};

	const onSearchChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		e.preventDefault();
		const newSearchId = Number(e.target.value);
		if (newSearchId === recPriceChangeSearchId) {
			dispatch(
				setQueryInput({
					searchId: newSearchId,
					searchCriteria: [
						{
							lhsCode: 'rec_rate',
							operatorCode: '!=',
							rhsCode: 'current_rate',
							rhsCustomValue: 0,
						},
					],
				}),
			);
		}
		dispatch(setSearchId(newSearchId));
		setIsFormChanged(false);
		setSaveSearchName('');
	};
	const onCreateSearch = () => {
		searchFormRef.current?.resetForm({ values: newSearch });
		setSearchDetails(newSearch);
	};
	const onReset = () => {
		setIsFormChanged(false);
		searchFormRef.current?.resetForm({ values: currentSearch });
		setSearchDetails(currentSearch);
		dispatch(setQueryInput(currentSearch));
	};
	let searchListOptions: menuType[] = [];
	if (userSearches) {
		const customSearches = userSearches
			.filter((search) => search?.isEditable)
			.sort((a, b) => {
				return (a?.searchId ?? 0) - (b?.searchId ?? 0);
			});
		const defaultSearches = userSearches
			.filter((search: UserSearch | undefined) => search && !search.isEditable)
			.sort((a, b) => {
				return (a?.searchId ?? 0) - (b?.searchId ?? 0);
			});
		const defaultIndex = defaultSearches.findIndex(
			(search) => search?.searchId === defaultSearchId,
		);
		const defaultSearch = defaultSearches.splice(defaultIndex, 1)[0];
		const sortedSearches = [
			defaultSearch,
			...defaultSearches,
			...customSearches,
		];
		searchListOptions = sortedSearches.map((option) => {
			return {
				value: option?.searchId?.toString() || '',
				label: option?.searchName || '',
				icon: option?.isEditable ? (
					<MdEdit
						size={15}
						color={styles.colorPrimary}
						onClick={onSearchClick}
					/>
				) : (
					''
				),
			};
		});
	}

	return (
		<div className={'tw-relative tw-pt-4 dashboard-layout '}>
			<RouterPromptModal isBlocked={isBlocked} />
			<Modal
				headerText={labels.rec_review.search.confirmation}
				isShown={isConfirmationModalOpen}
				hide={() => setIsConfirmationModalOpen(false)}
				onConfirm={() => {
					onClose();
					setIsConfirmationModalOpen(false);
					setIsBlocked(false);
				}}
				onCancel={onConfirmationWindowClose}
				isConfirmationModal={true}
				confirmBtnText={labels.rec_review.search.yes}
				cancelBtnText={labels.rec_review.search.no}
				messageBody={labels.rec_review.search.discard_search_msg}
				hasIcon={true}
			/>
			<Snackbar
				className='error-message'
				open={showConfirmMessage}
				autoHideDuration={3000}
				onClose={hideConfirmMessage}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Alert
					onClose={hideConfirmMessage}
					severity={info.messageType}
					sx={{ width: '100%' }}
				>
					{info.message}
				</Alert>
			</Snackbar>

			<div className='tw-flex tw-items-start tw-gap-6 tw-justify-between'>
				{/* AskRev component */}
				<div className='tw-flex-1'>
					<AskRev
						submitQuery={submitAskRevQuery}
						querySummary={askQuerySummary}
						suggestion={askRevSuggestion}
						compSet={compSet}
						value={selectedValue}
						onValueChange={(value) => {
							setSelectedValue(value);
							// Clear previous sort when selecting a new query
							if (value) {
								setAiSortBy(null);
								setAiSortDirection(null);
								setAiSortLimit(null);
							}
						}}
						queryHistoryData={queryHistoryData}
						onClear={handleClearAskRev}
						tabHint={askRevTabHint}
						aiSortBy={aiSortBy}
						aiSortDirection={aiSortDirection}
						aiSortLimit={aiSortLimit}
					/>
				</div>

				{/* Search component */}
				{!showSearch && (
					<div className='search tw-flex-shrink-0 tw-relative'>
						<div className='search__search-list'>
							<DropdownMenu
								options={searchListOptions}
								preSelectValue={searchId || defaultSearchId!}
								handleChange={onSearchChange}
								className='dashboard__search'
								loading={loadingUserSearches}
							/>
							<CustomTooltip
								content={
									<p>{labels.rec_review.search.tooltip.manage_search}</p>
								}
							>
								<span>
									<IconButton
										disableFocusRipple
										disableRipple
										aria-label='remove-search-criteria'
										onClick={onSearchClick}
										disabled={loadingUserSearches}
									>
										<MdManageSearch
											className='search__icon'
											size={37}
											color={'#905959'}
										/>
									</IconButton>
								</span>
							</CustomTooltip>

							<CustomTooltip content={<p>Bulk Rate Recommendations</p>}>
								<span>
									<IconButton
										disableFocusRipple
										disableRipple
										aria-label='bulk-rate-recommendations'
										onClick={onBulkFormToggle}
										disabled={loadingUserSearches}
									>
										<MdPlaylistAdd
											className='search__icon'
											size={37}
											color={showBulkForm ? '#4CAF50' : '#905959'}
										/>
									</IconButton>
								</span>
							</CustomTooltip>
						</div>
					</div>
				)}
			</div>

			{!showSearch && showSearchPanel && (
				<div className=''>
					<div className='tw-flex-1' />
					<div className='search__panel tw-w-auto tw-min-w-[400px] tw-flex-shrink-0'>
						<RecReviewSearch
							searchList={userSearches as UserSearch[]}
							id='recReviewSearch'
							formRef={searchFormRef}
							onSearch={onSearch}
							onSaveAs={onSearchSaveAs}
							onDelete={onDelete}
							onSave={onSearchSave}
							onCreate={onCreateSearch}
							onReset={onReset}
							searchMetrics={searchMetrics as SearchMetric[]}
							searchData={searchDetails}
							onClose={onClose}
							setIsBlocked={setIsBlocked}
							loading={saveSearchLoading || deleteLoading}
						/>
					</div>
				</div>
			)}

			{!showSearch && (
				<div
					className={`tw-w-full tw-mb-6 tw-transition-all tw-duration-300 tw-ease-in-out tw-overflow-hidden ${
						showBulkForm ? 'tw-max-h-[500px]' : 'tw-max-h-0'
					}`}
				>
					<BulkRateForm isVisible={showBulkForm} />
				</div>
			)}

			<div className='dashboard__wrapper'>
				<DashboardContent
					askRevFilters={askRevFilters}
					startDate={searchDetails.startDate}
					paginationMaxDuration={searchDetails.duration || 30}
					askRevQueryLoading={queryLoading}
					aiSortBy={aiSortBy}
					aiSortDirection={aiSortDirection}
					aiSortLimit={aiSortLimit}
				/>
			</div>
		</div>
	);
};
